import { useState } from 'react';
import { SectionSaveState } from './designTypes';
import { LOGO_SHAPES, BRAND_ICONS, FONT_OPTIONS } from './designConstants';
import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import { IdentitySettings } from './designTypes';

export function useSectionSave(onToast: (msg: string) => void) {
  const [state, setState] = useState<SectionSaveState>('idle');
  const save = async (fn: () => void | Promise<void>, successMsg: string) => {
    setState('saving');
    try {
      await fn();
      setState('saved');
      onToast(successMsg);
      setTimeout(() => setState('idle'), 2500);
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 2500);
    }
  };
  return { state, save };
}

export function SaveButton({ state, onClick, label = 'حفظ' }: {
  state: SectionSaveState; onClick: () => void; label?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={state === 'saving'}
      className={`mt-4 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${
        state === 'saved'
          ? 'bg-green-500 text-white'
          : state === 'error'
          ? 'bg-red-500 text-white'
          : 'bg-[#1a1a1a] hover:bg-[#333] text-white'
      }`}
    >
      {state === 'saving' ? (
        <i className="ri-loader-4-line animate-spin"></i>
      ) : state === 'saved' ? (
        <><i className="ri-checkbox-circle-line"></i> تم الحفظ!</>
      ) : state === 'error' ? (
        <><i className="ri-error-warning-line"></i> خطأ في الحفظ</>
      ) : (
        <><i className="ri-save-line"></i> {label}</>
      )}
    </button>
  );
}

export function ToggleRow({ icon, iconBg, label, desc, value, onChange }: {
  icon: string; iconBg: string; label: string; desc: string;
  value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 border border-gray-100">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${iconBg}`}>
          <i className={`${icon} text-sm`}></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-[#1a1a1a]">{label}</p>
          <p className="text-xs text-gray-400">{desc}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${value ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${value ? 'left-5' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

export function LogoPreview({ identity, selectedColor, selectedFont }: {
  identity: IdentitySettings;
  selectedColor: typeof COLOR_PRESETS[0];
  selectedFont: typeof FONT_OPTIONS[0];
}) {
  const icon = BRAND_ICONS.find(i => i.id === identity.logoIconId) ?? BRAND_ICONS[0];
  const textStyle = (() => {
    if (identity.logoTextStyle === 'gradient') return {
      background: `linear-gradient(to left, ${selectedColor.primaryLight}, ${selectedColor.primary})`,
      WebkitBackgroundClip: 'text' as const,
      WebkitTextFillColor: 'transparent' as const,
      backgroundClip: 'text' as const,
    };
    if (identity.logoTextStyle === 'white') return { color: '#9ca3af' };
    if (identity.logoTextStyle === 'gold')  return { color: '#f59e0b' };
    return { color: '#1a1a1a' };
  })();

  return (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${selectedColor.primaryLight}, ${selectedColor.primary})`,
          borderRadius: LOGO_SHAPES.find(s => s.id === identity.logoShape)?.radius ?? '16px',
        }}
      >
        {identity.useLogoImage && identity.logoImageUrl ? (
          <img src={identity.logoImageUrl} alt="logo" className="w-full h-full object-cover" />
        ) : (
          <i className={`${icon.icon} text-lg`} style={{ color: selectedColor.textOnPrimary }}></i>
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-lg font-black leading-none" style={{ fontFamily: selectedFont.css, ...textStyle }}>
          {identity.logoText || 'إبداع'}
        </span>
        {identity.logoSubtext && (
          <span className="text-[10px] font-medium tracking-widest" style={{ color: selectedColor.primary, opacity: 0.8 }}>
            {identity.logoSubtext}
          </span>
        )}
      </div>
    </div>
  );
}
